import { async } from "@firebase/util";
import { useEffect, useMemo, useRef, useState } from "react";
import { getAlgoliaKey } from "../firebase/functions";
import { getClient } from "../search";
import { useAuth } from "./useAuth";
import { InstantSearch, useSearchBox, UseSearchBoxProps } from "react-instantsearch-hooks";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import Input from "../components/inputs/Input";
import { FirestoreTrack } from "./useUpload";
import { doc } from "firebase/firestore";
import { UITrack } from "../firebase/firestore";
import SpotLoader from "../components/misc/SpotLoader";

export function hitToUiTrack(hit: any): UITrack {
  return { track: hit, ref: doc(db, hit.path), path: hit.path };
}

export function AlgoliaSearchContext({ index, secured = false, children, ...rest }) {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  useEffect(() => {
    (async () => {
      if (client) return;
      if (secured) {
        if (!user) return;
        let apiKey = user?.algoliaApiKey;
        if (!apiKey) {
          apiKey = await (await httpsCallable(functions, "getAlgoliaKey")()).data.key;
        }

        setClient(getClient(apiKey));
      } else {
        setClient(getClient());
      }
    })();
  }, [user, client]);

  if (!client) return <div />;
  return (
    <InstantSearch indexName={index} searchClient={client} {...rest}>
      {children}
    </InstantSearch>
  );
}
export function TrackSearchContext({ children, ...rest }) {
  return <AlgoliaSearchContext index="tracks" secured={true} children={children} {...rest} />;
}

export type SearchBoxProps = UseSearchBoxProps;

export function SearchBox(props: SearchBoxProps & { placeholder: string }) {
  const { query, refine, isSearchStalled } = useSearchBox(props);
  const [inputValue, setInputValue] = useState(query);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (inputRef.current) {
      inputRef.current.blur();
    }
  }

  function handleReset(event: React.FormEvent) {
    event.preventDefault();
    event.stopPropagation();

    setInputValue("");

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  // Track when the value coming from the React state changes to synchronize
  // it with InstantSearch.
  useEffect(() => {
    if (query !== inputValue) {
      refine(inputValue);
    }
  }, [inputValue, refine]);

  // Track when the InstantSearch query changes to synchronize it with
  // the React state.
  useEffect(() => {
    // Bypass the state update if the input is focused to avoid concurrent
    // updates when typing.
    if (document.activeElement !== inputRef.current && query !== inputValue) {
      setInputValue(query);
    }
  }, [query]);

  return (
    <div className="ais-SearchBox" style={{ margin: ".5rem 0px" }}>
      <form action="" className="ais-SearchBox-form" noValidate onSubmit={handleSubmit} onReset={handleReset}>
        <Input
          transparent
          ref={inputRef}
          className=" py-1 "
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          placeholder={props.placeholder}
          spellCheck={false}
          maxLength={512}
          type="search"
          value={inputValue}
          onChange={(event) => setInputValue(event.currentTarget.value)}
        />
      </form>
    </div>
  );
}
